// src/stage2/characters/ShadowOrbRig.jsx
import * as THREE from "three";
import React, { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";

/* ---------- util: feet-on-ground ---------- */
function fitAndGround(object) {
  const box = new THREE.Box3().setFromObject(object);
  const center = box.getCenter(new THREE.Vector3());
  object.position.sub(center);
  box.setFromObject(object);
  object.position.y -= box.min.y;
}

/* ---------- util: shadow-only prep ---------- */
function prepareForShadows(root, { shadowOnly = true } = {}) {
  root.traverse((obj) => {
    if (!obj.isMesh) return;

    obj.castShadow = true;
    obj.receiveShadow = false;

    if (shadowOnly && obj.material) {
      const hide = (m) => {
        m.transparent = true;
        m.opacity = 0.0;
        m.colorWrite = false;
        m.depthWrite = true;
        m.depthTest = true;
        m.shadowSide = THREE.FrontSide;
      };
      (Array.isArray(obj.material) ? obj.material : [obj.material]).forEach(hide);
    }

    const isSkinned = !!obj.isSkinnedMesh;
    const hasMorphs = !!obj.morphTargetInfluences;

    const depthMat = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking,
    });
    depthMat.skinning = isSkinned;
    depthMat.morphTargets = hasMorphs;
    depthMat.morphNormals = hasMorphs;
    obj.customDepthMaterial = depthMat;

    const distanceMat = new THREE.MeshDistanceMaterial();
    distanceMat.skinning = isSkinned;
    distanceMat.morphTargets = hasMorphs;
    distanceMat.morphNormals = hasMorphs;
    obj.customDistanceMaterial = distanceMat;

    if (obj.isSkinnedMesh) obj.frustumCulled = false;
  });
}

/* ---------- bone find (smart) ---------- */
function findFirstBoneSmart(root, names = []) {
  if (!root) return null;

  // try user-provided names first
  for (const n of names) {
    const obj = root.getObjectByName(n);
    if (obj) return obj;
  }

  // common Mixamo + generic fallbacks
  const fallback = [
    "mixamorigHead",
    "mixamorigSpine2",
    "mixamorigSpine1",
    "mixamorigSpine",
    "mixamorigHips",
    "Head",
    "Spine2",
    "Spine1",
    "Spine",
    "Hips",
  ];
  for (const n of fallback) {
    const obj = root.getObjectByName(n);
    if (obj) return obj;
  }
  return null;
}

/* ---------- Glow orb (copied behavior from Stage2Characters) ---------- */
function GlowOrb({
  anchorRef,
  fallbackRef,
  color = "#7df4ff",
  size = 0.45,
  pulse = true,
  offset = [0, 0.06, 0],
}) {
  const group = useRef();

  const haloMat = useMemo(() => {
    const tex = new THREE.TextureLoader().load(
      "data:image/svg+xml;utf8," +
        encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128">
            <radialGradient id="g" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stop-color="white" stop-opacity="1"/>
              <stop offset="100%" stop-color="white" stop-opacity="0"/>
            </radialGradient>
            <rect width="100%" height="100%" fill="url(#g)"/>
          </svg>`
        )
    );
    tex.colorSpace = THREE.SRGBColorSpace;
    return new THREE.SpriteMaterial({
      map: tex,
      color,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0.9,
    });
  }, [color]);

  const coreMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color,
        emissive: new THREE.Color(color),
        emissiveIntensity: 3,
        metalness: 0,
        roughness: 0.3,
      }),
    [color]
  );

  useFrame(({ clock }) => {
    if (!group.current) return;

    const pWorld = new THREE.Vector3();

    if (anchorRef?.current) {
      anchorRef.current.updateWorldMatrix(true, false);
      anchorRef.current.getWorldPosition(pWorld);
    } else if (fallbackRef?.current) {
      fallbackRef.current.updateWorldMatrix(true, true);
      const box = new THREE.Box3().setFromObject(fallbackRef.current);
      box.getCenter(pWorld);
      if (!Number.isFinite(pWorld.y)) return;
    } else return;

    // convert world -> local of orb parent
    const parent = group.current.parent;
    const pLocal = pWorld.clone();
    if (parent) parent.worldToLocal(pLocal);

    group.current.position.set(
      pLocal.x + offset[0],
      pLocal.y + offset[1],
      pLocal.z + offset[2]
    );

    if (pulse) {
      const t = clock.getElapsedTime();
      const s = size * (1 + 0.08 * Math.sin(t * 2.2));
      // sprite halo
      group.current.children[0].scale.setScalar(s * 2.2);
      // core mesh
      group.current.children[1].scale.setScalar(s);
    }
  });

  return (
    <group ref={group} renderOrder={2}>
      <sprite material={haloMat} />
      <mesh material={coreMat}>
        <sphereGeometry args={[1, 24, 24]} />
      </mesh>
    </group>
  );
}

/**
 * ShadowOrbRig
 * - preps rig for shadow-only (invisible mesh casting shadow)
 * - attaches GlowOrb (Stage2Characters style) to a bone
 */
export default function ShadowOrbRig({
  rig,
  baseScale = 1.0,
  shadowScaleXZ = [1, 1],
  shadowOnly = true,

  // glow orb props (Stage2Characters style)
  orb = true,
  orbColor = "#7df4ff",
  orbSize = 0.45,
  orbPulse = true,
  orbOffset = [0, 0.06, 0],
  orbBoneNames = ["mixamorigHead", "Head", "Spine2", "Hips"],

  children,
}) {
  const modelRef = useRef();
  const boneRef = useRef(null);
  const preppedRef = useRef(false);

  // prep rig once (prevents dev-mode “refresh to see shadow” weirdness)
  useEffect(() => {
    if (!rig) return;
    if (preppedRef.current) return;
    preppedRef.current = true;

    fitAndGround(rig);
    prepareForShadows(rig, { shadowOnly });

    const sx = shadowScaleXZ?.[0] ?? 1;
    const sz = shadowScaleXZ?.[1] ?? 1;
    rig.scale.set(baseScale * sx, baseScale, baseScale * sz);
  }, [rig, shadowOnly, baseScale, shadowScaleXZ]);

  // find orb bone whenever rig changes
  useEffect(() => {
    if (!rig) return;
    boneRef.current = orb ? findFirstBoneSmart(rig, orbBoneNames) : null;
    // console.log("[ShadowOrbRig] orb bone =", boneRef.current?.name);
    if (orb && !boneRef.current) {
      console.warn("[ShadowOrbRig] No orb bone found. Check orbBoneNames vs rig bone names.");
    }
  }, [rig, orb, orbBoneNames]);

  if (!rig) return null;

  return (
    <group ref={modelRef}>
      {/* rig (shadow-only, but still animated) */}
      {children ?? <primitive object={rig} />}

      {/* GlowOrb (same look/behavior as Stage2Characters) */}
      {orb && (
        <GlowOrb
          anchorRef={boneRef}
          fallbackRef={modelRef}
          color={orbColor}
          size={orbSize}
          pulse={orbPulse}
          offset={orbOffset}
        />
      )}
    </group>
  );
}
